import React from "react";
import { ContentBlock, isContentBlockRemovable } from "@/types/case-study";
import { TextBlock } from "./TextBlock";
import { ImageBlock } from "./ImageBlock";
import { VideoBlock } from "./VideoBlock";
import { GalleryBlock } from "./GalleryBlock";
import { CarouselBlock } from "./CarouselBlock";
import { LineBreakBlock } from "./LineBreakBlock";
import { QuoteBlock } from "./QuoteBlock";
import { CalloutBlock } from "./CalloutBlock";

interface ContentBlockRendererProps {
  block: ContentBlock;
  onUpdate: (content: any) => void;
  onRemove: () => void;
  isActive?: boolean;
  preview?: boolean;
  isRemovable?: boolean;
}

export const ContentBlockRenderer: React.FC<ContentBlockRendererProps> = ({
  block,
  onUpdate,
  onRemove,
  isActive = false,
  preview = false,
  isRemovable: isRemovableProp,
}) => {
  const handleUpdate = (content: any) => {
    onUpdate(content);
  };

  // Use provided isRemovable prop, or fall back to checking block type
  const isRemovable = isRemovableProp !== undefined ? isRemovableProp : isContentBlockRemovable(block.type);

  switch (block.type) {
    case "text":
      return (
        <TextBlock
          block={block}
          onUpdate={handleUpdate}
          onRemove={isRemovable ? onRemove : undefined}
          isActive={isActive}
          isRemovable={isRemovable}
        />
      );
    
    case "image":
      return (
        <ImageBlock
          block={block}
          onUpdate={handleUpdate}
          onRemove={isRemovable ? onRemove : undefined}
          isActive={isActive}
          isRemovable={isRemovable}
          preview={preview}
        />
      );
    
    case "video":
      return (
        <VideoBlock
          block={block}
          onUpdate={handleUpdate}
          onRemove={isRemovable ? onRemove : undefined}
          isActive={isActive}
          isRemovable={isRemovable}
        />
      );
    
    case "gallery":
      return (
        <GalleryBlock
          block={block}
          onUpdate={handleUpdate}
          onRemove={isRemovable ? onRemove : undefined}
          isActive={isActive}
          isRemovable={isRemovable}
        />
      );
    
    case "carousel":
      return (
        <CarouselBlock
          block={block}
          onUpdate={handleUpdate}
          onRemove={isRemovable ? onRemove : undefined}
          isActive={isActive}
          isRemovable={isRemovable}
          preview={preview}
        />
      );
    
    case "line-break":
      return (
        <LineBreakBlock
          block={block}
          onUpdate={handleUpdate}
          onRemove={isRemovable ? onRemove : undefined}
          isActive={isActive}
          isRemovable={isRemovable}
        />
      );
    
    case "quote":
      return (
        <QuoteBlock
          block={block}
          onUpdate={handleUpdate}
          onRemove={isRemovable ? onRemove : undefined}
          isActive={isActive}
          isRemovable={isRemovable}
        />
      );

    case "callout":
      return (
        <CalloutBlock
          block={block}
          onUpdate={handleUpdate}
          onRemove={isRemovable ? onRemove : undefined}
          isActive={isActive}
          isRemovable={isRemovable}
        />
      );

    default:
      console.warn(`Unknown content block type: ${block.type}`);
      return (
        <div className="p-4 border border-red-200 bg-red-50 rounded-lg">
          <p className="text-red-600 text-sm">
            Unknown content type: {block.type}
          </p>
        </div>
      );
  }
};

